import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Settings } from 'lucide-react';
import { useOwnerSettings } from '@/contexts/OwnerSettingsContext';

const STRATEGY_PARTNERS = ['Ken', 'Sean', 'Tim', 'Trav', 'Chase'];

export function SettingsDrawer() {
  const { disabledOwners, toggleOwner } = useOwnerSettings();

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Settings" className="relative">
          <Settings className="h-4 w-4" />
          {disabledOwners.length > 0 && (
            <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-accent text-[10px] text-accent-foreground flex items-center justify-center">
              {disabledOwners.length}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="px-6 pb-6">
        <SheetHeader className="px-0 pt-6 pb-4">
          <SheetTitle>Settings</SheetTitle>
        </SheetHeader>
        <div className="space-y-6">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-sm font-medium">Owner Profit Distribution</h3>
              {disabledOwners.length > 0 && (
                <Badge variant="secondary" className="bg-accent/10 text-accent border-accent/20">
                  {disabledOwners.length} excluded
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Enable/disable individual owners from receiving BASE profit share distributions (margin % from base revenue). Disabled owners will still receive operational compensation, tactical specialist pay, and performance bonuses.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {STRATEGY_PARTNERS.map(owner => {
                const isEnabled = !disabledOwners.includes(owner);

                return (
                  <div
                    key={owner}
                    onClick={() => toggleOwner(owner)}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      isEnabled
                        ? 'bg-card border-primary/40 hover:border-primary/60 shadow-sm'
                        : 'bg-muted border-border hover:border-muted-foreground/30 opacity-60'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`font-medium text-sm ${isEnabled ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {owner}
                      </span>
                      <div className={`w-2.5 h-2.5 rounded-full ${isEnabled ? 'bg-primary' : 'bg-muted-foreground/50'}`} />
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {isEnabled ? 'Active' : 'Excluded'}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                {STRATEGY_PARTNERS.length - disabledOwners.length} of {STRATEGY_PARTNERS.length} owners receiving distributions
              </span>
              {disabledOwners.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    // Clear all disabled owners by toggling each one that's disabled
                    disabledOwners.forEach(owner => toggleOwner(owner));
                  }}
                  className="h-7 text-xs"
                >
                  Enable All
                </Button>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
